import { useCallback, useMemo, useState } from 'react'
import { Box, Container, FormControlLabel, Grid, RadioGroup, Typography } from '@mui/material'
import { useRouter } from 'next/router'

import Button from '@/components/base/button'
import Form from '@/components/base/form'
import { Radio } from '@/components/base/radio'
import Spacer from '@/components/sections/spacer'
import { useNotification } from '@/contexts/notification'
import { isHappa } from '@/hooks'
import { useLocaleStore } from '@/store/locale'

import { useAuth } from '@/api/auth'
import { WPCF7FormField, WPLink, WPPageData } from '@/types'

export type LoginProps = {
  pageProps?: Omit<WPPageData, 'acf'> & {
    acf: {
      title: string
      text: string
      option_1: {
        title: string
        description: string
        url: WPLink
      }
      option_2: {
        title: string
        description: string
        url: WPLink
      }
      option_3: {
        title: string
        url: WPLink
      }
      sign_label: string
      form: {
        title: string
        text: string
        form: {
          fields: WPCF7FormField[]
          button: {
            text: string
          }
        }
      }
    }
  }
}

const Login = ({ pageProps }: LoginProps) => {
  const [selectedOption, setSelectedOption] = useState<'option_1' | 'option_2'>('option_1')
  const acf = useMemo(() => pageProps?.acf, [pageProps])
  const form = useMemo(() => acf?.form?.form, [acf])
  const fields = useMemo(() => form?.fields || [], [form])
  const selectedUrl = acf?.[selectedOption]?.url?.url || ''
  const router = useRouter()
  const currentLocale = useLocaleStore((state) => state.currentLocale)

  const { login } = useAuth()
  const { notifySuccess, notifyError } = useNotification()

  const handleSubmit = useCallback(
    async (values: Record<string, any>) => {
      try {
        // Extract email and password from form values
        let email = ''
        let password = ''
        fields.forEach((field) => {
          const inputKey = `input_${field.id}`
          if (field.adminLabel === 'email' && values[inputKey] !== undefined) {
            email = values[inputKey]
          }
          if (field.adminLabel === 'password' && values[inputKey] !== undefined) {
            password = values[inputKey]
          }
        })
        const payload = { email, password, locale: currentLocale }
        await login(payload)
        notifySuccess('Login effettuato con successo')
        router.replace('/auth/dashboard')
        return true
      } catch {
        notifyError('Login fallito. Controlla le tue credenziali e riprova.')
        return false
      }
    },
    [router, login, notifyError, notifySuccess, fields, currentLocale]
  )

  return (
    <>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 8, md: 6 }} sx={{ display: 'flex' }}>
            <Box
              sx={(theme) => ({
                p: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                borderRadius: theme.shape.borderRadius4,
                backgroundColor: theme.palette.surface.background,
                boxShadow: theme.shadows[2]
              })}
            >
              {acf?.title && (
                <Typography
                  variant="h4"
                  color={isHappa ? 'primary.main' : 'text.secondaryContrastText'}
                >
                  {acf.title}
                </Typography>
              )}
              <Spacer size="xxs" />
              {acf?.text && (
                <Typography variant="textRegular" color="text.primary">
                  {acf.text}
                </Typography>
              )}
              <Spacer size="xs" />
              <RadioGroup
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value as 'option_1' | 'option_2')}
              >
                {acf?.option_1 && (
                  <>
                    <Typography
                      variant="textLarge"
                      fontWeight="bold"
                      fontFamily={isHappa ? 'var(--heading-font)' : 'var(--base-font)'}
                    >
                      {acf.option_1.title}
                    </Typography>
                    <FormControlLabel
                      value="option_1"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="textRegular" color="text.secondaryContrastText">
                            {acf.option_1.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </>
                )}
                <Spacer size="xs" />
                {acf?.option_2 && (
                  <>
                    <Typography
                      variant="textLarge"
                      fontWeight="bold"
                      color="text.secondaryContrastText"
                      fontFamily={isHappa ? 'var(--heading-font)' : 'var(--base-font)'}
                    >
                      {acf.option_2.title}
                    </Typography>
                    <FormControlLabel
                      value="option_2"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="textRegular" color="text.secondaryContrastText">
                            {acf.option_2.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </>
                )}
              </RadioGroup>
              <Spacer size="xs" />
              {acf?.sign_label && selectedUrl && (
                <Button variant="contained" color="primary" href={selectedUrl} icon="user">
                  {acf.sign_label}
                </Button>
              )}
              <Spacer size="xs" />
              {acf?.option_2 && (
                <>
                  <Typography variant="textRegular" color="text.secondaryContrastText">
                    <a href={acf.option_3.url.url}>{acf.option_3.title}</a>
                  </Typography>
                </>
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 4, sm: 8, md: 6 }}>
            <Box
              sx={(theme) => ({
                p: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                borderRadius: theme.shape.borderRadius4,
                backgroundColor: theme.palette.surface.background,
                boxShadow: theme.shadows[2]
              })}
            >
              {acf?.form.title && (
                <Typography
                  variant="h4"
                  color={isHappa ? 'primary.main' : 'text.secondaryContrastText'}
                >
                  {acf.form.title}
                </Typography>
              )}
              <Spacer size="xxs" />
              {acf?.form?.text && (
                <Typography variant="textRegular" color="text.primary">
                  {acf.form.text}
                </Typography>
              )}
              <Spacer size="xs" />
              <Form
                form_id={form?.fields[0].formId.toString() || ''}
                fields={form?.fields || []}
                submit={{
                  color: isHappa ? 'secondary' : 'warning',
                  text: form?.button?.text ?? ''
                }}
                onSubmit={handleSubmit}
                customEndpoint={true}
                useGravity={false}
                bypassPasswordValidationEmail="admin@studiogusto.it"
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Spacer size="2xl" />
    </>
  )
}

export default Login
